import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalLinks from '../LegalLinks';
import { useLanguage } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────────────
// Copyright / DMCA Notices — the takedown-request page a DMCA filing points at.
//
// Route: /dmca (exactly that path — it is what the U.S. Copyright Office
// filing references, so it is a constant like ADDENDUM_PATH, not a choice).
//
// 🛑 THE BODY TEXT IS VERBATIM and must match the filed notice. This mirrors
// DataAiAddendumPage's rule: layout may change, wording may not. Two
// transcription notes, both faithfulness-over-tidiness:
//   • The source has "counter- notification" with a line-wrap space in the
//     middle of the word; rendered here as the single word "counter-notification".
//   • "30N Gould St" (no space between "30" and "N") is kept exactly as given,
//     because it must match the filing — even though the Addendum's own
//     address block a few lines away in this same file spells it "30 N Gould
//     St". Two different documents, two different transcriptions of the same
//     street; neither is "fixed" here.
//
// PUBLIC, like the Addendum: a DMCA notice is filed by people who have never
// used PAI and have no account, so this cannot sit behind a login.
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_TITLE = 'Copyright / DMCA Notices';

// Shared text rhythm — copied from DataAiAddendumPage so the two legal pages
// read as one family.
const P = 'mt-4 text-[15px] leading-[1.75] text-pai-body';
const H2 = 'mt-10 text-[20px] font-extrabold tracking-[-0.02em] text-white';

// A numbered requirement item (the "your notice must include" list).
const Item = ({ n, children }) => (
    <li className="relative pl-9 text-[15px] leading-[1.75] text-pai-body">
        <span className="absolute left-0 top-0 font-mono text-[13px] text-pai-meta">{n}.</span>
        {children}
    </li>
);

const DmcaPage = () => {
    const { t } = useLanguage();

    useEffect(() => {
        const previous = document.title;
        document.title = PAGE_TITLE;
        return () => { document.title = previous; };
    }, []);

    return (
        <div
            data-testid="legal-dmca-page"
            className="relative min-h-dvh bg-pai-void text-white"
        >
            <div
                aria-hidden="true"
                className="fixed inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('/wallpaper.jpg')" }}
            />
            <div aria-hidden="true" className="fixed inset-0 bg-[rgba(5,7,10,0.78)]" />

            <div className="relative mx-auto w-full max-w-[860px] px-6 py-10 sm:py-14">
                {/* Masthead — identical pattern to the Addendum: a reader
                    arriving here (usually from a rights-holder's own notice
                    workflow) has no app chrome around them. */}
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span
                            aria-hidden="true"
                            className="flex h-10 w-10 flex-none select-none items-center justify-center rounded-full bg-white text-[13px] font-extrabold tracking-[-0.02em] text-pai-void"
                        >
                            PAI
                        </span>
                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-pai-meta">
                            {t('legalEyebrow', 'Legal')}
                        </span>
                    </div>
                    <Link
                        to="/"
                        data-testid="legal-dmca-back"
                        className="rounded-ctl border border-pai-subtle bg-white/[0.04] px-4 py-2 text-[13px] font-semibold text-pai-secondary transition-colors hover:border-pai-strong hover:text-white"
                    >
                        {t('legalBackToPai', 'Back to PAI')}
                    </Link>
                </div>

                <article className="mt-8 rounded-modal border border-white/[0.14] bg-white/[0.03] px-6 py-9 backdrop-blur-tile sm:px-11 sm:py-12">
                    <h1
                        data-testid="legal-dmca-title"
                        className="text-[32px] font-extrabold leading-[1.15] tracking-[-0.03em] text-white sm:text-[38px]"
                    >
                        Copyright / DMCA Notices
                    </h1>

                    <p className={P}>
                        The Title AI LLC (&quot;PAI&quot;) responds to notices of alleged copyright
                        infringement under the U.S. Digital Millennium Copyright Act (DMCA),
                        17 U.S.C. § 512.
                    </p>
                    <p className={P}>
                        If you believe that material stored on our service infringes your copyright,
                        please send a written notice to our designated agent:
                    </p>

                    <address className="mt-6 not-italic text-[15px] leading-[1.75] text-pai-body rounded-card border border-pai-hairline bg-white/[0.02] px-5 py-5">
                        <span className="block font-bold text-white">DMCA Agent</span>
                        The Title AI LLC<br />
                        30N Gould St, STE R<br />
                        Sheridan, WY 82801, USA<br />
                        Email:{' '}
                        <a
                            href="mailto:admin@workbypai.com"
                            className="text-pai-blue underline underline-offset-2 hover:text-pai-blue-tint"
                        >
                            admin@workbypai.com
                        </a>
                        <br />
                        Phone:{' '}
                        <a
                            href="tel:+19012064749"
                            className="text-pai-blue underline underline-offset-2 hover:text-pai-blue-tint"
                        >
                            901-206-4749
                        </a>
                    </address>

                    <h2 className={H2}>Your notice must include:</h2>
                    <ol className="mt-4 space-y-3">
                        <Item n={1}>
                            Identification of the copyrighted work you claim has been infringed.
                        </Item>
                        <Item n={2}>
                            Identification of the material you want removed and information reasonably
                            sufficient to allow us to locate it on the service.
                        </Item>
                        <Item n={3}>
                            Your name, address, telephone number, and email address.
                        </Item>
                        <Item n={4}>
                            A statement that you have a good-faith belief that the use of the material
                            is not authorized by the copyright owner, its agent, or the law.
                        </Item>
                        <Item n={5}>
                            A statement, made under penalty of perjury, that the information in your
                            notice is accurate and that you are the copyright owner or authorized to
                            act on the copyright owner&apos;s behalf.
                        </Item>
                        <Item n={6}>Your physical or electronic signature.</Item>
                    </ol>

                    <p className={P}>
                        Upon receipt of a valid notice, we may remove or disable access to the
                        identified material and notify the affected user. Users who believe their
                        content was removed in error may submit a counter-notification in accordance
                        with 17 U.S.C. § 512(g).
                    </p>
                    <p className={P}>
                        It is the policy of The Title AI LLC to terminate, in appropriate
                        circumstances, the accounts of users who are repeat infringers.
                    </p>
                </article>

                <LegalLinks className="mt-8" />
            </div>
        </div>
    );
};

export default DmcaPage;
